import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';
import { fetchCart, quantityFixes } from '@theme/uradora-cart';

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/** The gallery only reacts to choices on desktop, where it stays in view beside the steps. */
const desktop = window.matchMedia('(min-width: 990px)');

/** How far the page can scroll before an option's photo gives way to the product again. */
const OPTION_PHOTO_SCROLL = 48;

/** Line item property names saved on the box. */
const PROPERTY_MESSAGE = 'Message';
const PROPERTY_DATE = 'Delivery date';

/**
 * Shopify's /cart/add.js won't take more than this many of a nested line in one request,
 * whatever the stock (it adds this many and returns an error), while /cart/update.js sets
 * any quantity. Nested lines go in at up to this many and are then raised to the box
 * quantity with an update.
 */
const NESTED_ADD_LIMIT = 50;

/** @param {Date} date */
const isoDate = (date) => date.toISOString().slice(0, 10);

/**
 * A YYYY-MM-DD date as dd/mm/yyyy, the way the team reads delivery dates.
 * @param {string} iso
 */
const displayDate = (iso) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  });

/** @param {HTMLInputElement | null | undefined} input */
const priceOf = (input) => Number(input?.dataset.price) || 0;

/**
 * Today's date where the shop is, as midnight UTC, so date maths can stay in UTC.
 * @param {string} [offset] - The shop's UTC offset, such as +0800
 */
function shopToday(offset) {
  const match = /^([+-])(\d{2})(\d{2})$/.exec(offset ?? '');
  const minutes = match
    ? (match[1] === '-' ? -1 : 1) * (Number(match[2]) * 60 + Number(match[3]))
    : -new Date().getTimezoneOffset();
  const now = new Date(Date.now() + minutes * 60000);
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
}

/**
 * @param {Date} start
 * @param {number} days - Working days to add; weekends and holidays don't count
 * @param {Set<string>} holidays - Dates as YYYY-MM-DD
 */
function addWorkingDays(start, days, holidays) {
  const date = new Date(start);
  let added = 0;

  while (added < days) {
    date.setUTCDate(date.getUTCDate() + 1);
    const weekday = date.getUTCDay();
    if (weekday !== 0 && weekday !== 6 && !holidays.has(isoDate(date))) added += 1;
  }

  return date;
}

/**
 * One image set of the gallery. Slides sit in a scroll-snap track, so touch swipes work
 * natively; the arrows and thumbnails drive the same track. When the thumbnails don't all
 * fit, their strip scrolls sideways and follows the slide that's showing.
 */
class UradoraCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-gallery-track]');
    this.slides = [...this.querySelectorAll('[data-gallery-slide]')];
    this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
    this.strip = this.querySelector('[data-gallery-thumbs]');
    this.stripPrev = this.querySelector('[data-gallery-strip-prev]');
    this.stripNext = this.querySelector('[data-gallery-strip-next]');
    this.index = 0;

    if (!this.track) return;

    this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.go(this.index - 1));
    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.go(this.index + 1));
    this.thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => this.go(index)));
    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    if (this.strip) {
      this.stripPrev?.addEventListener('click', () => this.scrollStrip(-1));
      this.stripNext?.addEventListener('click', () => this.scrollStrip(1));
      this.strip.addEventListener('scroll', this.onStripScroll, { passive: true });
      this.stripObserver = new ResizeObserver(() => this.updateStrip());
      this.stripObserver.observe(this.strip);
    }

    const start = this.slides.findIndex((slide) => slide.hasAttribute('data-gallery-start'));
    if (start > 0) {
      this.go(start, 'instant');
    } else {
      this.mark(0, 'instant');
    }
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
    this.strip?.removeEventListener('scroll', this.onStripScroll);
    this.stripObserver?.disconnect();
  }

  onStripScroll = () => {
    if (this.stripFrame) return;

    this.stripFrame = requestAnimationFrame(() => {
      this.stripFrame = null;
      this.updateStrip();
    });
  };

  /** Shows the strip's arrows and faded edges only while some thumbnails are out of view. */
  updateStrip() {
    const { scrollLeft, scrollWidth, clientWidth } = this.strip;
    const overflows = scrollWidth > clientWidth + 1;
    const atStart = scrollLeft <= 1;
    const atEnd = scrollLeft + clientWidth >= scrollWidth - 1;

    const more = [];
    if (overflows && !atStart) more.push('start');
    if (overflows && !atEnd) more.push('end');
    this.strip.dataset.more = more.join(' ');

    for (const arrow of [this.stripPrev, this.stripNext]) {
      if (arrow) arrow.hidden = !overflows;
    }
    if (this.stripPrev) this.stripPrev.disabled = atStart;
    if (this.stripNext) this.stripNext.disabled = atEnd;
  }

  /** @param {number} direction - -1 to scroll back, 1 to scroll on */
  scrollStrip(direction) {
    this.strip.scrollBy({
      left: direction * this.strip.clientWidth * 0.8,
      behavior: reducedMotion.matches ? 'instant' : 'smooth',
    });
  }

  /**
   * Centres a thumbnail in the strip when it's out of view. Only the strip scrolls, never the page.
   * @param {number} index
   * @param {ScrollBehavior} behavior
   */
  revealThumb(index, behavior) {
    const thumb = this.thumbs[index];
    if (!this.strip || !thumb) return;

    const { scrollLeft, clientWidth } = this.strip;
    const left = thumb.offsetLeft;
    const right = left + thumb.offsetWidth;
    if (left >= scrollLeft && right <= scrollLeft + clientWidth) return;

    this.strip.scrollTo({
      left: left - (clientWidth - thumb.offsetWidth) / 2,
      behavior: reducedMotion.matches ? 'instant' : behavior,
    });
  }

  onScroll = () => {
    if (this.frame) return;

    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      const index = Math.round(this.track.scrollLeft / this.track.clientWidth);
      if (index !== this.index) this.mark(index);
    });
  };

  /**
   * @param {number} index - Slide to show; wraps around at either end.
   * @param {ScrollBehavior} [behavior]
   */
  go(index, behavior = 'smooth') {
    const count = this.slides.length;
    if (count === 0) return;

    const target = (index + count) % count;
    this.track.scrollTo({
      left: target * this.track.clientWidth,
      behavior: reducedMotion.matches ? 'instant' : behavior,
    });
    this.mark(target, behavior);
  }

  /**
   * @param {number | string} mediaId
   * @param {ScrollBehavior} [behavior]
   */
  showMedia(mediaId, behavior) {
    const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
    if (index >= 0 && index !== this.index) this.go(index, behavior);
  }

  /**
   * @param {number} index
   * @param {ScrollBehavior} [behavior] - How the thumbnail strip catches up
   */
  mark(index, behavior = 'smooth') {
    this.index = index;
    this.thumbs.forEach((thumb, position) => {
      thumb.setAttribute('aria-current', String(position === index));
    });
    this.revealThumb(index, behavior);
  }
}

/**
 * Stacks the product's carousel with one per option step (fillings, personal touches) and
 * crossfades between them. The product's photos show unless an option was just clicked.
 */
class UradoraGallery extends HTMLElement {
  connectedCallback() {
    this.sets = new Map(
      [...this.querySelectorAll('uradora-carousel')].map((carousel) => [carousel.dataset.gallerySet, carousel])
    );
    this.active = [...this.sets.keys()].find((name) => this.sets.get(name).classList.contains('is-active'));
  }

  /** @param {string} name */
  show(name) {
    if (!this.sets.has(name) || name === this.active) return;

    for (const [key, carousel] of this.sets) {
      const active = key === name;
      carousel.classList.toggle('is-active', active);
      carousel.inert = !active;
    }

    this.active = name;
  }

  /**
   * Brings a set forward, optionally moving it to one image first. A set that isn't showing
   * yet jumps straight to the image so it fades in already there.
   * @param {string} name
   * @param {number | string} [mediaId]
   */
  reveal(name, mediaId) {
    const carousel = this.sets.get(name);
    if (!carousel) return;

    if (mediaId) carousel.showMedia(mediaId, name === this.active ? 'smooth' : 'instant');
    this.show(name);
  }
}

/**
 * Holds the configurator state for one product: the choices, the summary, adding to the cart
 * and, on desktop, showing a clicked option's photo in the gallery.
 */
class UradoraProduct extends HTMLElement {
  connectedCallback() {
    const data = this.querySelector('[data-uproduct-json]');
    this.variants = data ? JSON.parse(data.textContent).variants : [];
    this.variantInput = this.querySelector('[data-uproduct-variant]');
    this.gallery = this.querySelector('uradora-gallery');
    this.optionInputs = [...this.querySelectorAll('[data-option-position]')];
    this.variant = this.variants.find((variant) => String(variant.id) === this.variantInput?.value) ?? null;

    this.messagePanel = this.querySelector('[data-message-panel]');
    this.messageInput = this.querySelector('[data-message-input]');
    this.messageCount = this.querySelector('[data-message-count]');
    this.quantityInput = this.querySelector('[data-quantity-input]');
    this.quantityNote = this.querySelector('[data-quantity-note]');
    this.quantityStock = this.querySelector('[data-quantity-stock]');
    this.quantityButtons = [...this.querySelectorAll('[data-quantity-step]')];
    this.datePanel = this.querySelector('[data-date-panel]');
    this.dateInput = this.querySelector('[data-date-input]');
    this.dateHint = this.querySelector('[data-date-hint]');

    this.form = this.variantInput?.form ?? null;
    this.summary = this.querySelector('[data-summary]');
    this.addButton = this.querySelector('[data-add-to-cart]');
    this.addLabel = this.querySelector('[data-add-label]');
    this.buttonText = this.addLabel?.textContent ?? '';
    this.cartError = this.querySelector('[data-cart-error]');
    this.messageError = this.querySelector('[data-message-error]');
    this.pageWrapper = document.querySelector('.page-wrapper');

    this.addEventListener('change', this.onChange);
    this.addEventListener('input', this.onInput);
    this.form?.addEventListener('submit', this.onSubmit);
    this.dateInput?.addEventListener('change', this.checkDate);
    this.setDateRange();
    this.messageInput?.addEventListener('input', this.updateMessageCount);
    this.quantityInput?.addEventListener('input', this.capQuantity);
    this.quantityInput?.addEventListener('change', this.normaliseQuantity);
    this.quantityButtons.forEach((button) => button.addEventListener('click', this.stepQuantity));
    this.pageWrapper?.addEventListener('scroll', this.onPageScroll, { passive: true });
    window.addEventListener('scroll', this.onPageScroll, { passive: true });
    desktop.addEventListener('change', this.showProductPhotos);
    this.refreshAvailability();
    this.refreshQuantityLimit();
    this.updateMessageCount();
    this.refresh();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    this.removeEventListener('input', this.onInput);
    this.form?.removeEventListener('submit', this.onSubmit);
    this.messageInput?.removeEventListener('input', this.updateMessageCount);
    this.quantityInput?.removeEventListener('input', this.capQuantity);
    this.quantityInput?.removeEventListener('change', this.normaliseQuantity);
    this.quantityButtons.forEach((button) => button.removeEventListener('click', this.stepQuantity));
    this.dateInput?.removeEventListener('change', this.checkDate);
    this.pageWrapper?.removeEventListener('scroll', this.onPageScroll);
    window.removeEventListener('scroll', this.onPageScroll);
    desktop.removeEventListener('change', this.showProductPhotos);
    clearTimeout(this.flagTimer);
    clearTimeout(this.addedTimer);
  }

  /** Keeps the summary and Add to Cart in step with the selection. */
  refresh() {
    this.renderSummary();
    this.refreshBuyState();
  }

  /** @param {Event} event */
  onInput = (event) => {
    if (event.target === this.messageInput) this.clearFieldError(this.messageInput, this.messageError);
    this.refresh();
  };

  /** @param {SubmitEvent} event */
  onSubmit = (event) => {
    event.preventDefault();
    this.addToCart();
  };

  /** @param {string} role - contents, personalise or delivery */
  choice(role) {
    return /** @type {HTMLInputElement | null} */ (this.querySelector(`[data-choice="${role}"]:checked`));
  }

  /** Whether the chosen delivery method asks for a date. */
  get wantsDate() {
    const delivery = this.choice('delivery');
    return Boolean(delivery && this.datePanel && delivery.value === this.datePanel.dataset.opensFor);
  }

  /** Everything the customer has chosen. A personal touch is null when they picked "No thanks". */
  get selection() {
    const personalise = this.choice('personalise');
    const touch = personalise?.value ? personalise : null;

    return {
      variant: this.variant,
      contents: this.choice('contents'),
      personalise: touch,
      delivery: this.choice('delivery'),
      message: touch ? (this.messageInput?.value.trim() ?? '') : '',
      date: this.wantsDate ? (this.dateInput?.value ?? '') : '',
      quantity: this.quantity,
    };
  }

  /** @param {number} cents */
  money(cents) {
    return formatMoney(cents, this.dataset.moneyFormat || '{{amount}}', this.dataset.currency || 'SGD');
  }

  renderSummary() {
    const summary = this.summary;
    if (!summary) return;

    const { variant, contents, personalise, delivery, message, date, quantity } = this.selection;

    const variantLabel = summary.querySelector('[data-summary-variant]');
    if (variantLabel && variant) variantLabel.textContent = variant.title;

    this.fillSummaryRow('contents', contents, contents?.dataset.note);
    this.fillSummaryRow('personalise', personalise, message || personalise?.dataset.note);
    this.fillSummaryRow(
      'delivery',
      delivery,
      date ? (summary.dataset.deliveryBy ?? '[date]').replace('[date]', displayDate(date)) : delivery?.dataset.note
    );

    const unit = (variant?.price ?? 0) + priceOf(contents) + priceOf(personalise);
    const charge = priceOf(delivery);
    const unitLabel = quantity === 1 ? summary.dataset.unitOne : summary.dataset.unitOther;

    const boxes = summary.querySelector('[data-summary-boxes]');
    if (boxes) boxes.textContent = `${quantity} ${unitLabel} x ${this.money(unit)}`;

    const chargeLine = summary.querySelector('[data-summary-charge]');
    if (chargeLine instanceof HTMLElement) {
      chargeLine.hidden = charge === 0;
      chargeLine.textContent = (summary.dataset.charge ?? '').replace('[amount]', this.money(charge));
    }

    const total = summary.querySelector('[data-summary-total]');
    if (total) total.textContent = this.money(unit * quantity + charge);
  }

  /**
   * @param {string} role
   * @param {HTMLInputElement | null} input - The chosen option; the row hides without one
   * @param {string} [note] - Second line under the option's name
   */
  fillSummaryRow(role, input, note) {
    const row = this.summary?.querySelector(`[data-summary-row="${role}"]`);
    if (!(row instanceof HTMLElement)) return;

    row.hidden = !input;
    if (!input) return;

    const amount = priceOf(input);
    const title = row.querySelector('[data-summary-title]');
    const detail = row.querySelector('[data-summary-note]');
    const price = row.querySelector('[data-summary-price]');

    if (title) title.textContent = input.dataset.title ?? '';
    if (detail) detail.textContent = note ?? '';
    if (price) price.textContent = amount === 0 && role === 'delivery' ? (this.summary?.dataset.free ?? '') : `+${this.money(amount)}`;
  }

  /** The first thing stopping this selection from going in the cart, or null when it's ready. */
  get problem() {
    const selection = this.selection;
    const text = this.summary?.dataset ?? {};

    if (!selection.variant?.available) return { step: 'packaging', message: text.textSoldOut };
    if (this.querySelector('[data-ustep="contents"]') && !selection.contents) {
      return { step: 'contents', message: text.errorContents };
    }
    if (this.querySelector('[data-ustep="personalise"]') && !this.choice('personalise')) {
      return { step: 'personalise', message: text.errorPersonalise };
    }
    if (selection.personalise && !selection.message) {
      return { step: 'personalise', message: text.errorMessage, field: this.messageInput, fieldError: this.messageError };
    }
    if (this.querySelector('[data-ustep="delivery"]') && !selection.delivery) {
      return { step: 'delivery', message: text.errorDelivery };
    }
    if (this.wantsDate && !selection.date) {
      return { step: 'delivery', message: text.errorDate, field: this.dateInput, fieldError: this.dateHint };
    }

    return null;
  }

  /**
   * Shows what's missing beside the field it belongs to, under the step's title, or under the
   * buttons, and takes the customer to that step.
   * @param {{ step: string, message?: string, field?: HTMLElement | null, fieldError?: HTMLElement | null }} problem
   */
  report(problem) {
    const step = this.querySelector(`[data-ustep="${problem.step}"]`);
    const stepError = step?.querySelector('[data-step-error]');

    if (problem.field && problem.fieldError) {
      problem.fieldError.textContent = problem.message ?? '';
      problem.fieldError.hidden = false;
      problem.field.setAttribute('aria-invalid', 'true');
    } else if (stepError instanceof HTMLElement) {
      stepError.textContent = problem.message ?? '';
      stepError.hidden = false;
    } else {
      this.showCartError(problem.message);
    }

    step?.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
    const target = problem.field ?? step?.querySelector('input:not(:disabled)');
    if (target instanceof HTMLElement) target.focus({ preventScroll: true });
  }

  /**
   * @param {HTMLElement | null} field
   * @param {HTMLElement | null} error
   */
  clearFieldError(field, error) {
    if (!field?.hasAttribute('aria-invalid')) return;
    field.removeAttribute('aria-invalid');
    if (error) error.hidden = true;
  }

  /** @param {string} [message] */
  showCartError(message) {
    if (!this.cartError) return;
    this.cartError.textContent = message || this.summary?.dataset.errorGeneric || '';
    this.cartError.hidden = false;
  }

  clearCartError() {
    if (this.cartError) this.cartError.hidden = true;
  }

  /** Disables Add to Cart for a sold out variant. */
  refreshBuyState() {
    const available = Boolean(this.variant?.available);

    if (this.addButton instanceof HTMLButtonElement && !this.busy) {
      this.addButton.disabled = !available;
      if (this.addLabel) this.addLabel.textContent = available ? this.buttonText : (this.summary?.dataset.textSoldOut ?? '');
    }
  }

  /**
   * The cart lines for this selection: the box with the message and delivery date as visible
   * properties, then its filling, personal touch and delivery nested under it. Fillings and
   * personal touches match the box quantity; delivery is once per configured box line.
   */
  cartItems() {
    const { variant, contents, personalise, delivery, message, date, quantity } = this.selection;
    const parent = Number(variant?.id);

    /** @type {Record<string, string>} */
    const properties = {};
    if (message) properties[PROPERTY_MESSAGE] = message;
    if (date) properties[PROPERTY_DATE] = displayDate(date);

    const items = [{ id: parent, quantity, properties }];
    const nest = (/** @type {HTMLInputElement | null} */ input, /** @type {string} */ role, /** @type {number} */ count) => {
      if (input) items.push({ id: Number(input.value), quantity: count, parent_id: parent, properties: { _role: role } });
    };

    nest(contents, 'contents', quantity);
    nest(personalise, 'personalise', quantity);
    nest(delivery, 'delivery', 1);

    return items;
  }

  async addToCart() {
    const problem = this.problem;
    if (problem) {
      this.report(problem);
      return;
    }
    if (this.busy) return;

    this.busy = true;
    this.clearCartError();
    clearTimeout(this.addedTimer);
    this.addButton?.setAttribute('aria-busy', 'true');
    if (this.addLabel) this.addLabel.textContent = this.summary?.dataset.textAdding ?? this.buttonText;

    const items = this.cartItems();
    const sections = [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
      .filter(Boolean);
    const detail = {
      source: 'uradora-product',
      sourceId: this.dataset.productId,
      productId: this.dataset.productId,
      itemCount: items[0].quantity,
    };
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    let added = false;

    try {
      const request = items.map((item) =>
        item.parent_id ? { ...item, quantity: Math.min(item.quantity, NESTED_ADD_LIMIT) } : item
      );
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: request, sections: sections.join(',') }),
      });
      const result = await response.json();
      let cart = await fetchCart();

      if (!result.status) {
        const fixes = quantityFixes(cart);
        if (Object.keys(fixes).length > 0) {
          const update = await fetch(`${Theme.routes.cart_update_url}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ updates: fixes, sections: sections.join(','), sections_url: window.location.pathname }),
          });
          const updated = await update.json();
          if (!updated.status) {
            cart = updated;
            result.sections = updated.sections;
          }
        }
      }

      if (result.status) {
        this.showCartError(result.description || result.message);
        this.dispatchEvent(
          new CartErrorEvent({
            error: result.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: result.description, errors: result.errors },
          })
        );
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { ...detail, items: cart.items, didError: true },
        });
        return;
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { ...detail, items: cart.items, sections: result.sections, didError: false },
      });
      added = true;
    } catch (error) {
      console.error(error);
      deferred.reject(error);
      this.showCartError();
      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      this.busy = false;
      this.addButton?.setAttribute('aria-busy', 'false');
      this.refreshBuyState();

      if (added && this.addLabel) {
        this.addLabel.textContent = this.summary?.dataset.textAdded ?? this.buttonText;
        this.addedTimer = setTimeout(() => this.refreshBuyState(), 2000);
      }
    }
  }

  /** Number of boxes; fillings and personal touches always follow it. */
  get quantity() {
    return Number(this.quantityInput?.value) || 1;
  }

  /**
   * The most boxes the current selection allows. Fillings and personal touches go in at the
   * box quantity, so their stock caps it along with the packaging's and the order maximum.
   * `byStock` is true when stock, not the order maximum, sets the limit.
   */
  get quantityLimit() {
    const cap = Number(this.quantityInput?.dataset.max) || Infinity;
    const perBox = this.querySelectorAll('[data-choice="contents"]:checked, [data-choice="personalise"]:checked');
    const stocks = [this.variant?.stock, ...[...perBox].map((input) => input.dataset.stock)]
      .filter((stock) => stock !== undefined && stock !== null && stock !== '')
      .map(Number);
    const lowestStock = Math.min(...stocks);

    return { max: Math.min(cap, lowestStock), byStock: lowestStock < cap };
  }

  /** Applies the current limit to the input, lowering the quantity if it's now over. */
  refreshQuantityLimit() {
    const input = this.quantityInput;
    if (!input) return;

    const min = Number(input.min) || 1;
    const { max, byStock } = this.quantityLimit;
    this.limitedByStock = byStock && max >= min;
    input.max = String(Math.max(max, min));

    if (this.quantityStock && this.limitedByStock) {
      this.quantityStock.textContent = (this.quantityStock.dataset.template ?? '').replace('[count]', String(max));
    }

    const over = Number(input.value) > Number(input.max);
    if (over) input.value = input.max;

    this.refreshQuantityState();
    if (over) this.flagQuantityLimit();
  }

  /**
   * Greys out a stepper button that can't go further, and shows the low stock message only
   * once the quantity has reached what's in stock.
   */
  refreshQuantityState() {
    const input = this.quantityInput;
    if (!input) return;

    const value = Number(input.value);
    const atMax = value >= Number(input.max);

    for (const button of this.quantityButtons) {
      const blocked = Number(button.dataset.quantityStep) < 0 ? value <= Number(input.min) : atMax;
      button.setAttribute('aria-disabled', String(blocked));
    }

    if (this.quantityStock) this.quantityStock.hidden = !(this.limitedByStock && atMax);
  }

  /** @param {MouseEvent} event */
  stepQuantity = (event) => {
    const input = this.quantityInput;
    if (!input || !(event.currentTarget instanceof HTMLElement)) return;

    const min = Number(input.min) || 1;
    const max = Number(input.max) || Infinity;
    const step = Number(input.step) || 1;
    const next = (Number(input.value) || min) + Number(event.currentTarget.dataset.quantityStep) * step;

    input.value = String(Math.min(Math.max(next, min), max));
    input.dispatchEvent(new Event('input', { bubbles: true }));
    if (next > max) this.flagQuantityLimit();
  };

  /** Stops the quantity at the limit as the customer types, pointing them to the reason. */
  capQuantity = () => {
    const input = this.quantityInput;
    if (!input) return;

    const over = input.value !== '' && Number(input.value) > Number(input.max);
    if (over) input.value = input.max;

    this.refreshQuantityState();
    if (over) this.flagQuantityLimit();
  };

  /** Settles the quantity on a whole number within the allowed range once editing ends. */
  normaliseQuantity = () => {
    const input = this.quantityInput;
    if (!input) return;

    const min = Number(input.min) || 1;
    const max = Number(input.max) || Infinity;
    const step = Number(input.step) || 1;
    const value = Math.floor(Number(input.value));

    if (!Number.isFinite(value) || value < min) {
      input.value = String(min);
    } else {
      const stepped = min + Math.round((value - min) / step) * step;
      input.value = String(Math.min(stepped, max));
    }

    this.refreshQuantityState();
  };

  /** Draws attention to whichever message explains the limit: low stock or the order maximum. */
  flagQuantityLimit() {
    const target = this.quantityStock && !this.quantityStock.hidden ? this.quantityStock : this.quantityNote;
    if (!target) return;

    target.classList.remove('is-flagged');
    void target.offsetWidth;
    target.classList.add('is-flagged');
    clearTimeout(this.flagTimer);
    this.flagTimer = setTimeout(() => target.classList.remove('is-flagged'), 2400);
  }

  /** @param {Event} event */
  onChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    this.clearCartError();

    if (input.dataset.optionPosition) {
      this.selectVariant();
      return;
    }

    if (input === this.dateInput) input.removeAttribute('aria-invalid');

    const stepError = input.dataset.choice ? input.closest('[data-ustep]')?.querySelector('[data-step-error]') : null;
    if (stepError instanceof HTMLElement) stepError.hidden = true;

    if (input.dataset.choice === 'personalise' && this.messagePanel) {
      this.messagePanel.hidden = input.value === '';
      if (input.value === '') this.clearFieldError(this.messageInput, this.messageError);
    }

    if (input.dataset.choice === 'contents' || input.dataset.choice === 'personalise') {
      this.refreshQuantityLimit();
    }

    if (input.dataset.choice === 'delivery' && this.datePanel) {
      this.datePanel.hidden = input.value !== this.datePanel.dataset.opensFor;
    }

    if (input.dataset.gallerySet === 'product') {
      this.showProductPhotos();
    } else if (input.dataset.gallerySet) {
      this.showOptionPhoto(input.dataset.gallerySet, input.dataset.galleryMedia);
    }

    this.refresh();
  };

  /** How far the page has scrolled; desktop scrolls the page wrapper, phones the window. */
  get scrollPosition() {
    return Math.max(this.pageWrapper?.scrollTop ?? 0, window.scrollY);
  }

  /**
   * Shows a filling's or personal touch's photo after it's clicked. Desktop only; the
   * product's own photos come back once the customer scrolls on.
   * @param {string} set
   * @param {string} [mediaId]
   */
  showOptionPhoto(set, mediaId) {
    if (!desktop.matches) return;
    this.gallery?.reveal(set, mediaId);
    this.optionPhotoScroll = this.scrollPosition;
  }

  showProductPhotos = () => {
    this.optionPhotoScroll = undefined;
    this.gallery?.show('product');
  };

  onPageScroll = () => {
    if (this.optionPhotoScroll === undefined) return;
    if (Math.abs(this.scrollPosition - this.optionPhotoScroll) < OPTION_PHOTO_SCROLL) return;
    this.showProductPhotos();
  };

  /** Limits the delivery date to between the earliest date the shop can make and a year after it. */
  setDateRange() {
    if (!this.datePanel || !this.dateInput) return;

    const holidays = new Set((this.datePanel.dataset.holidays ?? '').split(/[\s,]+/).filter(Boolean));
    const leadDays = Number(this.datePanel.dataset.leadDays) || 12;
    const earliest = addWorkingDays(shopToday(this.datePanel.dataset.shopOffset), leadDays, holidays);
    const latest = new Date(earliest);
    latest.setUTCFullYear(latest.getUTCFullYear() + 1);

    this.dateInput.min = isoDate(earliest);
    this.dateInput.max = isoDate(latest);
  }

  /** Moves a typed date that's too soon up to the earliest date, and says why. */
  checkDate = () => {
    const input = this.dateInput;
    if (!input) return;

    const tooEarly = input.value !== '' && input.value < input.min;
    if (tooEarly) input.value = input.min;
    if (input.value > input.max) input.value = input.max;

    if (this.dateHint) {
      this.dateHint.hidden = !tooEarly;
      if (tooEarly) {
        const earliest = new Date(`${input.min}T00:00:00Z`).toLocaleDateString(undefined, {
          weekday: 'short',
          day: 'numeric',
          month: 'short',
          year: 'numeric',
          timeZone: 'UTC',
        });
        this.dateHint.textContent = (this.dateHint.dataset.template ?? '').replace('[date]', earliest);
      }
    }
  };

  updateMessageCount = () => {
    if (!this.messageInput || !this.messageCount) return;

    const count = this.messageInput.value.length;
    const template = this.messageCount.dataset.template ?? '[count]';
    this.messageCount.textContent = template.replace('[count]', String(count));
    this.messageCount.classList.toggle('is-full', count >= this.messageInput.maxLength);
  };

  /** The checked value for each option, in option order. */
  get selectedOptions() {
    const positions = [...new Set(this.optionInputs.map((input) => input.dataset.optionPosition))];
    return positions.map(
      (position) => this.optionInputs.find((input) => input.dataset.optionPosition === position && input.checked)?.value
    );
  }

  selectVariant() {
    const options = this.selectedOptions;
    const variant = this.variants.find((candidate) => candidate.options.every((value, i) => value === options[i]));

    this.variant = variant ?? null;

    if (variant) {
      if (this.variantInput) this.variantInput.value = String(variant.id);
      if (variant.media && desktop.matches) {
        this.optionPhotoScroll = undefined;
        this.gallery?.reveal('product', variant.media);
      }

      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variant.id));
      window.history.replaceState(window.history.state, '', url);
    }

    this.refreshAvailability();
    this.refreshQuantityLimit();
    this.refresh();
  }

  /**
   * Marks option values with nothing purchasable behind them, given the values already
   * chosen for the options before them.
   */
  refreshAvailability() {
    const selected = this.selectedOptions;
    const multipleOptions = selected.length > 1;

    for (const input of this.optionInputs) {
      const position = Number(input.dataset.optionPosition) - 1;
      const available = this.variants.some(
        (variant) =>
          variant.available &&
          variant.options[position] === input.value &&
          variant.options.slice(0, position).every((value, i) => value === selected[i])
      );

      const card = input.closest('.uvariant');
      card?.classList.toggle('is-unavailable', !available);

      const status = card?.querySelector('[data-option-status]');
      if (status instanceof HTMLElement) status.hidden = available;

      // With several options, each value's price depends on what's chosen for the others.
      const price = card?.querySelector('[data-option-price]');
      if (multipleOptions && price instanceof HTMLElement) {
        const match = this.variants.find((variant) =>
          variant.options.every((value, i) => (i === position ? value === input.value : value === selected[i]))
        );
        price.hidden = !match;
        if (match) price.textContent = `${this.money(match.price)}${price.dataset.suffix ? ` ${price.dataset.suffix}` : ''}`;
      }
    }
  }
}

if (!customElements.get('uradora-carousel')) {
  customElements.define('uradora-carousel', UradoraCarousel);
}

if (!customElements.get('uradora-gallery')) {
  customElements.define('uradora-gallery', UradoraGallery);
}

if (!customElements.get('uradora-product')) {
  customElements.define('uradora-product', UradoraProduct);
}
